import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';

import { alertService } from '@unpispas/upp-base';
import { clockService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { languageSupport } from '@unpispas/upp-base';
import { PlacePanel } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { AskWaiterView } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { Ticket } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { UPP_TABLE_CHAIRS_MAX } from '@unpispas/upp-data';
import { UPP_TABLE_CHAIRS_MIN } from '@unpispas/upp-data';

type SelectOption = {
    value: any;
    text: string;
};

type TableAmountParts = {
    Int: string;
    Dec: string;
};

type TableAttentionBrief = {
    isgeneric: boolean;
    refund: number;
    torefund: boolean;
    pending: number;
    paycash: number;
    givechange: number;
    backchange: number;
    paycard: number;
    amount: number;
};

@Component({
    selector: 'upp-table-edit',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-edit.html',
    styleUrls: [
        './upp-edit.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]
})
export class UppTableEditComponent extends languageSupport implements OnInit, OnChanges, OnDestroy {
    /***********************************************/
    /* INPUTS AND OUTPUTS                          */
    /***********************************************/

    @Input() place: PlaceView | null = null;
    @Input() table: QrCodeView | null = null;

    @Output() ToPanel = new EventEmitter<PlacePanel>();

    /***********************************************/
    /* SUBSCRIPTION MEMBER VARIABLES               */
    /***********************************************/

    private _table_subscription: Subscription | null = null;
    private _close_subscription: Subscription | null = null;
    private _clock_subscription: Subscription | null = null;

    /***********************************************/
    /* OTHER CLASS VARIABLES                       */
    /***********************************************/

    private _table_qr_task = 0;
    public TableQrDataUrl: string | null = null;
    private _attentionLatch = false;
    private _waitRequestDone = false;
    private _requests: Array<AskWaiterView> | null = null;
    private _pending: Array<TicketView> | null = null;
    private _brief: TableAttentionBrief | null = null;

    /***********************************************/
    /* GENERAL UI STATE                            */
    /***********************************************/

    readonly TableChairsMin = UPP_TABLE_CHAIRS_MIN;
    readonly TableChairsMax = UPP_TABLE_CHAIRS_MAX;

    get ShowAreas(): boolean {
        return this.ShowDraw || this.ShowPrices || this.ShowTills;
    }

    get CanAcceptChanges(): boolean {
        return this.TableForm.dirty && this.TableForm.valid;
    }

    /***********************************************/
    /* FORM MANAGEMENT                             */
    /***********************************************/

    private _tableform: FormGroup | null = null;

    private _TillValidators(_form: FormGroup): void {
        const _till = _form.get('till');
        if (!_till){
            return;
        }

        if (this.ShowTills){
            _till.setValidators([Validators.required]);
        }
        else {
            _till.clearValidators();
        }
        
        _till.updateValueAndValidity({ emitEvent: false });
    }

    private get AreaOption(): SelectOption | null {
        return this.table?.area ? {
            value: this.table.area,
            text: this.table.area.name?.trim() || this.lang.tr('@place_table_area_noname'),
        } : null;
    }

    private get PriceOption(): SelectOption | null {
        if (!this.table?.pricezone){
            return null;
        }

        return {
            value: this.table.pricezone,
            text: this.table.pricezone.Label,
        };
    }

    private get TillOption(): SelectOption | null {
        if (!this.table?.till){
            return null;
        }

        return {
            value: this.table?.till,
            text: this.table?.till.Label,
        };
    }

    private LoadTableForm(): FormGroup {
        const _alias: string | null = this.table?.alias || null;
        const _chairsRaw = Number(this.table?.chairs);
        const _chairs = (_chairsRaw > 0) ? _chairsRaw : 4;
        const _area: SelectOption | null = this.AreaOption;
        const _price: SelectOption | null = this.PriceOption;
        const _till: SelectOption | null = this.TillOption;

        let _form: FormGroup | null = this._tableform;
        if (_form == null){
            _form = new FormGroup({
                alias: new FormControl(_alias, []),
                chairs: new FormControl(_chairs, [
                    Validators.required,
                    Validators.min(this.TableChairsMin),
                    Validators.max(this.TableChairsMax),
                ]),
                area: new FormControl(_area, []),
                price: new FormControl(_price, []),
                till: new FormControl(_till, [])
            });
        }
        else {
            _form.patchValue({
                alias: _alias,
                chairs: _chairs,
                area: _area,
                price: this.PriceOption,
                till: this.TillOption
            });
        }

        this.change.markForCheck();

        this._TillValidators(_form);

        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0);

        return _form;
    }

    get TableForm(): FormGroup {
        if (!this._tableform){
            this._tableform = this.LoadTableForm();
        }

        return this._tableform;
    }

    /***********************************************/
    /* CONSTRUCTOR AND LIFECYCLE HANDLERS          */
    /***********************************************/

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private alertCtrl: alertService,
        private data: dataService,
        private clock: clockService,
        public view: viewService,
    ){
        super(lang, change.cdref);
    }

    ngOnInit(): void {
        this._close_subscription = this.view.OnCloseRequest.subscribe(async () => {
            this.view.OnCloseResponse(await this.OnCloseRequest());
        });

        this._clock_subscription = this.clock.OnOneMinuteTick.subscribe(() => {
            this.change.markForCheck();
        });
    }

    ngOnChanges(): void {
        this.TableQrDataUrl = null;
        this._tillOptions = null;
        this._priceOptions = null;
        this._InvalidateAttention();

        if (this._table_subscription){
            this._table_subscription.unsubscribe();
            this._table_subscription = null;
        }

        this._tableform = this.LoadTableForm();
        this._attentionLatch = !!this.table?.Attention;

        if (this.table){
            this._table_subscription = this.table.OnViewRefresh.subscribe(
            () => {
                this._InvalidateAttention();
                this._CloseIfAttentionCleared();
                this.change.markForCheck();
                if (!this.Attention){
                    this.RefreshTableQr();
                }
            });

            if (!this.Attention){
                this.RefreshTableQr();
            }
        }
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._table_subscription){
            this._table_subscription.unsubscribe();
            this._table_subscription = null;
        }

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this._clock_subscription){
            this._clock_subscription.unsubscribe();
            this._clock_subscription = null;
        }
    }

    /***********************************************/
    /* QR VISUALIZATION                            */
    /***********************************************/

    get ShowTableQr(): boolean {
        return !!this.TableQrDataUrl;
    }

    DownloadTableQr(): void {
        if (!this.TableQrDataUrl || !this.table){
            return;
        }

        const _place = this.place?.name || 'place';
        const _number = this.table.number || '-';
        const _filename = _place + ' - ' + _number + ' - qrcode.png';

        const _link = document.createElement('a');
        _link.href = this.TableQrDataUrl;
        _link.download = _filename;

        document.body.appendChild(_link);
        _link.click();
        document.body.removeChild(_link);
    }

    private static readonly _MARK_ASSET = 'assets/image/unpispas-mark.png';
    /** Cached mark for QR center; `undefined` until first fetch attempt. */
    private _markDataUrl: string | null | undefined = undefined;

    private async RefreshTableQr(): Promise<void> {
        const _task = ++this._table_qr_task;
        const _table = this.table;
        if (!_table){
            this.TableQrDataUrl = null;
            this.change.markForCheck();
            return;
        }

        const _lang = this.lang.GetLanguage(true);
        const _mark = await this.EnsureMarkDataUrl();
        if (this._table_qr_task !== _task){
            return;
        }

        const _qrcode = await _table.QrCodeImageDataUrl(_lang, {
            logoDataUrl: _mark,
        });
        if (this._table_qr_task !== _task){
            return;
        }

        this.TableQrDataUrl = _qrcode;
        this.change.markForCheck();
    }

    /**
     * Loads the Unpispas mark once for centered QR composition (same asset as sticker cells).
     */
    private async EnsureMarkDataUrl(): Promise<string | null> {
        if (this._markDataUrl !== undefined){
            return this._markDataUrl;
        }

        this._markDataUrl = await this.FetchImageAsDataUrl(UppTableEditComponent._MARK_ASSET);
        return this._markDataUrl;
    }

    private async FetchImageAsDataUrl(path: string): Promise<string | null> {
        try {
            const _response = await fetch(path);
            if (!_response.ok){
                return null;
            }

            const _blob = await _response.blob();
            return await new Promise<string | null>((resolve) => {
                const _reader = new FileReader();
                _reader.onload = () => resolve(typeof _reader.result === 'string' ? _reader.result : null);
                _reader.onerror = () => resolve(null);
                _reader.readAsDataURL(_blob);
            });
        }
        catch {
            return null;
        }
    }

    
    /***********************************************/
    /* DRAW AREA                                   */
    /***********************************************/

    /**
     * TODO: construir opciones desde `place.DrawAreas` (mapear a `SelectOption`; `value` según modelo
     * de área de plano / `FLOORAREA`). De momento array vacío.
     */
    get AreaOptions(): Array<SelectOption> {
        return [];
    }

    get ShowDraw(): boolean {
        return (this.place?.DrawAreas?.length ?? 0) > 0;
    }

    /***********************************************/
    /* PRICE AREA                                  */
    /***********************************************/

    private _priceOptions: Array<SelectOption> | null = null;

    get PriceOptions(): Array<SelectOption> {
        if (!this._priceOptions){
            const _options: Array<SelectOption> = [];

            for (const _zone of this.place?.PriceAreas ?? []){
                _options.push({
                    value: _zone,
                    text: _zone.Label,
                });
            }

            _options.sort((_left, _right) => _left.text.localeCompare(_right.text));
            this._priceOptions = _options;
        }

        return this._priceOptions;
    }

    get ShowPrices(): boolean {
        return (this.place?.PriceAreas?.length ?? 0) > 0;
    }

    /***********************************************/
    /* TILL AREA                                   */
    /***********************************************/

    private _tillOptions: Array<SelectOption> | null = null;

    get TillOptions(): Array<SelectOption> {
        if (!this._tillOptions){
            const _options: Array<SelectOption> = [];

            for (const _till of this.place?.TillAreas ?? []){
                _options.push({
                    value: _till,
                    text: _till.Label,
                });
            }

            _options.sort((_left, _right) => _left.text.localeCompare(_right.text));
            this._tillOptions = _options;
        }

        return this._tillOptions;
    }

    OptionTrack(_index: number, option: SelectOption): string | null {
        return option?.value?._uuid || null;
    }

    get ShowTills(): boolean {
        return (this.place?.TillAreas?.length ?? 0) > 1;
    }

    /***********************************************/
    /* ATTENTION PANEL                             */
    /***********************************************/

    get Attention(): boolean {
        return this._attentionLatch || !!this.table?.Attention;
    }

    get Requests(): Array<AskWaiterView> {
        if (this._requests == null){
            const _requests: Array<AskWaiterView> = [];
            for (const _request of this.table?.requests ?? []){
                if (_request.IsValid && _request.IsRecent){
                    _requests.push(_request);
                }
            }

            _requests.sort((left, right) => {
                return (right.created?.getTime() || 0) - (left.created?.getTime() || 0);
            });

            this._requests = _requests;
        }

        return this._requests;
    }

    get Pending(): Array<TicketView> {
        if (this._pending == null){
            const _pending: Array<TicketView> = [];
            for (const _ticket of this.table?.tickets ?? []){
                if (!_ticket.IsValid || !_ticket.IsRecent){
                    continue;
                }

                if (!_ticket.IsPaid || (!_ticket.IsDirect && !_ticket.IsReady)){
                    _pending.push(_ticket);
                }
            }

            this._pending = _pending;
        }

        return this._pending;
    }

    get Brief(): TableAttentionBrief {
        if (this._brief == null){
            const _brief: TableAttentionBrief = {
                isgeneric: true,
                refund: 0,
                torefund: false,
                pending: 0,
                paycash: 0,
                givechange: 0,
                backchange: 0,
                paycard: 0,
                amount: 0,
            };

            for (const _request of this.Requests){
                if (_request.reason != null){
                    _brief.isgeneric = false;
                }

                switch (_request.reason){
                    case 'REFUND':
                        _brief.refund += Number(_request.amount);
                        if (_request.ticket?.payment === 'PAYCARD'){
                            _brief.paycard -= Number(_request.amount);
                        }
                        if (_request.ticket?.payment === 'PAYCASH'){
                            _brief.paycash -= Number(_request.amount);
                        }
                        break;

                    case 'PAYCARD':
                        _brief.pending += Number(_request.ticket?.price);
                        _brief.paycard += Number(_request.ticket?.price);
                        break;

                    case 'PAYCASH':
                        _brief.pending += Number(_request.ticket?.price);
                        _brief.paycash += Number(_request.ticket?.price);
                        _brief.givechange += this._GiveChange(_request);
                        _brief.backchange += this._BackChange(_request);
                        break;
                }
            }

            _brief.pending = Math.round(Math.abs(_brief.pending) * 100) / 100;
            _brief.givechange = Math.round(Math.abs(_brief.givechange) * 100) / 100;
            _brief.backchange = Math.round(Math.abs(_brief.backchange) * 100) / 100;
            _brief.amount = _brief.pending - _brief.refund;
            _brief.torefund = _brief.amount < 0;
            _brief.amount = Math.round(Math.abs(_brief.amount) * 100) / 100;
            this._brief = _brief;
        }

        return this._brief;
    }

    RequestTrack(_index: number, request: AskWaiterView): string | null {
        return request?._uuid || null;
    }

    PendingTrack(_index: number, ticket: TicketView): string | null {
        return ticket?._uuid || null;
    }

    RequestElapsed(request: AskWaiterView): string {
        return this._ElapsedLabel(request.created, '@elapsed_time_just_now', '@elapsed_time_minutes', '@elapsed_time_hours');
    }

    TicketElapsed(ticket: TicketView): string {
        return this._ElapsedLabel(ticket.created, '@created_time_just_now', '@created_time_minutes', '@created_time_hours');
    }

    RequestAmount(request: AskWaiterView): TableAmountParts {
        return this._FormatAmount(request.amount || 0);
    }

    /***********************************************/
    /* PUBLIC HANDLERS                             */
    /***********************************************/

    async AcceptChanges(): Promise<void> {
        if (!this.table){
            return;
        }

        this.table.alias = this.TableForm.get('alias')?.value || null;
        this.table.chairs = Number(this.TableForm.get('chairs')?.value) || 4;
        
        if (this.ShowDraw){
            this.table.area = this.TableForm.get('area')?.value?.value || null;
        }
        
        if (this.ShowPrices){
            this.table.pricezone = this.TableForm.get('price')?.value?.value || null;
        }
        
        if (this.ShowTills){
            this.table.till = this.TableForm.get('till')?.value?.value || null;
        }

        this.TableForm.markAsPristine();
        await this.table.DoCommit();
        this._OnClose();
    }

    async CancelChanges(): Promise<void> {
        if (this.Attention || !this.TableForm.dirty || await this._ConfirmClose()){
            this._OnClose();
        }
    }

    /**
     * Opens a new ticket for this table from the waiter-attention panel.
     */
    OnPlaceOrder(): void {
        if (!this.place || !this.table || !this.place.AllowTicketCommit){
            return;
        }

        const _ticket = new Ticket(null, this.data);
        _ticket.created = new Date();
        _ticket.updated = new Date();
        _ticket.place = this.data.place;
        _ticket.qrcode = this.table.object;
        _ticket.session = this.data.session;
        _ticket.price = 0;
        _ticket.refund = 0;
        _ticket.taxes = 0;
        _ticket.status = 'PR';

        this.ToPanel.emit({
            main: 'cart',
            scnd: this.view.Desktop ? 'ticket' : null,
            args: {
                ticket: this.data.alives.get(_ticket) as TicketView,
                mode: 'EDIT'
            }
        });
    }

    OnShowTicket(ticket: TicketView | null): void {
        if (!ticket){
            return;
        }

        this.ToPanel.emit({
            scnd: 'ticket',
            args: {
                ticket: this.data.alives.get(ticket.Copy()) as TicketView,
                mode: 'VIEW'
            }
        });
    }

    async RequestDone(): Promise<void> {
        if (this._waitRequestDone){
            return;
        }

        if (this.Brief.amount == 0){
            await this._OnRequestDone();
            return;
        }

        const _message = this.Brief.torefund
            ? this.lang.tr('@place_table_request_confirm_refund', [ this.Brief.amount.toFixed(2), this.lang.tr('@currency') ])
            : this.lang.tr('@place_table_request_confirm_income', [ this.Brief.amount.toFixed(2), this.lang.tr('@currency') ]);

        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_table_request_confirm'),
            message: _message,
            backdropDismiss: true,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => alert.dismiss(true), 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => alert.dismiss(false), 100);
                    return false;
                }
            }]
        });

        await alert.present();
        const detail = await alert.onDidDismiss();
        if (detail?.data){
            await this._OnRequestDone();
        }
    }

    private async OnCloseRequest(): Promise<boolean> {
        if (this.Attention || !this.TableForm.dirty) {
            return true;
        }

        return this._ConfirmClose();
    }

    /***********************************************/
    /* PRIVATE METHODS                             */
    /***********************************************/

    private _OnClose(): void {
        this.TableForm.markAsPristine();
        this.ToPanel.emit({
            scnd: null,
            args: null
        });
    }

    private async _ConfirmClose(): Promise<boolean> {
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@changes_lost_confirm_tittle'),
            message: this.lang.tr('@changes_lost_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => alert.dismiss(true), 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => alert.dismiss(false), 100);
                    return false;
                }
            }]
        });

        await alert.present();
        const detail = await alert.onDidDismiss();
        return !!detail?.data;
    }

    private _InvalidateAttention(): void {
        this._requests = null;
        this._pending = null;
        this._brief = null;
    }

    private _CloseIfAttentionCleared(): void {
        if (this._attentionLatch && !this.table?.Attention){
            this._OnClose();
        }
        this._attentionLatch = !!this.table?.Attention;
    }

    private _GiveChange(request: AskWaiterView): number {
        return Number(request.ticket?.given) || 0;
    }

    private _BackChange(request: AskWaiterView): number {
        const _given = this._GiveChange(request);
        if (_given > 0 && request.ticket){
            return Math.round((_given - Number(request.ticket.price)) * 100) / 100;
        }
        return 0;
    }

    private _ElapsedLabel(from: Date | null | undefined, justNow: string, minutesKey: string, hoursKey: string): string {
        if (!from){
            return this.lang.tr(justNow);
        }

        const _min = Math.round((Date.now() - from.getTime()) / 60000);
        if (_min < 1){
            return this.lang.tr(justNow);
        }

        if (_min < 60){
            return this.lang.tr(minutesKey, [ _min ]);
        }

        const _hh = Math.round(_min / 60);
        const _mm = Math.round(_min % 60);
        return this.lang.tr(hoursKey, [ _hh, _mm ]);
    }

    private _FormatAmount(amount: number): TableAmountParts {
        const _parts = Math.abs(amount).toString().split('.');
        return {
            Int: _parts[0],
            Dec: (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : '00',
        };
    }

    private _ApplyRefund(ticket: TicketView): void {
        for (const _product of ticket.products){
            if (_product.status === 'UN'){
                _product.status = 'DE';
            }
        }

        for (const _offer of ticket.offers){
            if (_offer.status === 'UN'){
                _offer.status = 'DE';
            }
        }

        if (ticket.empty){
            ticket.status = 'DE';
        }
    }

    private async _OnRequestDone(): Promise<void> {
        if (this._waitRequestDone){
            return;
        }

        this._waitRequestDone = true;
        try {
            const _transaction = this.data.transaction;
            for (const _request of this.Requests){
                if (_request.reason === 'REFUND' && _request.ticket){
                    this._ApplyRefund(_request.ticket);
                    await _transaction.push(_request.ticket.object);
                }

                if ((_request.reason === 'PAYCARD' || _request.reason === 'PAYCASH') && _request.ticket){
                    const _ticket = _request.ticket;
                    await _ticket.DoPayment(_request.reason);
                    if (_ticket.IsEmpty){
                        console.error('[TABLE] The ticket is empty');
                        await _transaction.push(_ticket.object);
                    }
                    else {  // Already MarkPaid — CommitTicket assigns S/T + TicketChange F + TBAI.
                        await _ticket.OnCommit(null, _transaction);
                    }
                }

                _request.status = 'DE';
                await _transaction.push(_request.object);
            }

            await _transaction.flush();
            this._OnClose();
        }
        finally {
            this._waitRequestDone = false;
        }
    }
}
